/**
 * BoletoModal — dados do boleto de uma fatura e download.
 *
 * **Não cancela boleto.** Boleto só é cancelado pelo cancelamento da FATURA: quem tem boleto
 * dispara CANCEL_BILL ao biller e a fatura só fecha quando o BOLETO_CANCELED volta. Ter dois
 * caminhos para o mesmo efeito convidava a cancelar o boleto e deixar a fatura em cobrança.
 * Ver `CancelInvoiceModal`.
 *
 * Download: pede ao biller um link temporário (5 min) do PDF e abre em nova aba.
 * Prefere o boletoId (correlation_id do biller) e cai no nosso número quando ele falta —
 * os dois resolvem o mesmo boleto lá, mas o correlation_id é o identificador que ESTA fatura
 * gerou, enquanto o nosso número é atribuído depois, na emissão.
 */
import { useState } from 'react';
import { FaTimes, FaBarcode, FaDownload, FaSyncAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { checkBoletoLiquidation, getBoletoDownloadUrl } from '../services/billingApi';

interface Props {
    invoice: any;
    onClose: () => void;
    /** avisa quem abriu para recarregar — a mudança pode chegar em segundos, por evento */
    onChanged?: () => void;
}

const BoletoModal: React.FC<Props> = ({ invoice, onClose, onChanged }) => {
    const boleto = invoice.boleto || {};
    const [checking, setChecking] = useState(false);
    const [downloading, setDownloading] = useState(false);

    /** o que o biller entende como id do boleto; sem nenhum dos dois não há o que baixar */
    const billRef: string | undefined = boleto.boletoId || boleto.ourNumber || undefined;

    /**
     * O link do S3 é temporário e o bucket não tem CORS: por isso pedimos a URL e ABRIMOS, em vez de
     * baixar o PDF por fetch. `noopener` porque a aba aberta não deve poder mexer nesta.
     */
    const downloadBoleto = async () => {
        if (!billRef) return;
        setDownloading(true);
        try {
            const url = await getBoletoDownloadUrl(billRef);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e: any) {
            toast.error(e?.message || 'Falha ao obter o link do boleto');
        } finally {
            setDownloading(false);
        }
    };

    /**
     * Atualizar a situação é ASSÍNCRONO: o backend só publica o pedido, e o biller consulta o banco
     * e — apenas se a situação mudou — publica BOLETO_PAID/BOLETO_CANCELED. O texto tem de dizer
     * isso, senão o usuário clica, nada muda na hora e ele conclui que o botão não funciona.
     *
     * Existe porque as duas atualizações automáticas não cobrem o intervalo: o cron do biller roda
     * seg–sex 09:00 (hoje DISABLED) e o webhook do Itaú ainda não é funcional.
     */
    const refreshSituation = async () => {
        setChecking(true);
        try {
            await checkBoletoLiquidation(invoice.invoiceId);
            toast.success('Consulta enviada ao banco. A situação é atualizada em instantes, '
                          + 'e a fatura muda sozinha se o boleto tiver sido pago ou cancelado.');
            onChanged?.();
        } catch (e: any) {
            toast.error(e?.message || 'Falha ao pedir a consulta ao banco');
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2" title={invoice.invoiceId}>
                        <FaBarcode className="text-blue-500" />
                        Boleto — Fatura {invoice.invoiceId ? invoice.invoiceId.slice(0, 8) : ''}
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="mb-4 text-sm space-y-1">
                    <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Nosso número</span>
                        <span className="font-mono text-gray-900 dark:text-gray-100">{boleto.ourNumber || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Id do boleto</span>
                        <span className="font-mono text-xs text-gray-900 dark:text-gray-100 break-all text-right max-w-[240px]">{boleto.boletoId || '-'}</span>
                    </div>
                </div>

                <button
                    onClick={refreshSituation}
                    disabled={checking || !(boleto.ourNumber || boleto.boletoId)}
                    title={boleto.ourNumber || boleto.boletoId
                        ? 'Consulta a situação no banco. A fatura é atualizada por evento, em instantes'
                        : 'Sem nosso número nem id do boleto não há o que consultar no banco'}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <FaSyncAlt className={checking ? 'animate-spin' : ''} />
                    {checking ? 'Consultando o banco...' : 'Atualizar situação no banco'}
                </button>

                <button
                    onClick={downloadBoleto}
                    disabled={!billRef || downloading}
                    title={billRef
                        ? 'Abre o PDF do boleto em nova aba. O link vale por alguns minutos'
                        : 'Esta fatura não tem boleto emitido'}
                    className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
                >
                    <FaDownload />
                    {!billRef
                        ? 'Sem boleto para baixar'
                        : downloading ? 'Gerando link...' : 'Baixar boleto'}
                </button>

                <p className="text-xs text-gray-500 mt-3">
                    Para parar de cobrar este boleto, cancele a fatura — o cancelamento é enviado ao
                    banco e a fatura fecha quando o banco confirma.
                </p>
            </div>
        </div>
    );
};

export default BoletoModal;
